import { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { IoMdArrowBack } from "react-icons/io";
import { BeatLoader } from "react-spinners";
import toast from "react-hot-toast";
import { changeEmail, verifyEmailOtp } from "../../redux/ApiSlice/AccountSettingSlice";
import { AccountProfile } from "../../redux/ApiSlice/AccountProfileSlice";

// Timer duration in seconds (5 minutes)
const RESEND_DURATION = 300;

const VerifyEmail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { User_profile } = useSelector((state) => state.AccountProfile);
    const newEmail = location.state?.newEmail || "";
    const targetEmail = (newEmail || User_profile?.email || "").trim();

    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const inputRefs = useRef([]);
    const [loading, setLoading] = useState(false);
    const [timer, setTimer] = useState(RESEND_DURATION);
    const [resending, setResending] = useState(false);

    // Timer countdown effect
    useEffect(() => {
        if (timer > 0) {
            const interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [timer]);

    // Format timer as mm:ss
    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    // Resend OTP handler
    const handleResendOtp = async () => {
        setResending(true);
        try {
            if (!targetEmail) {
                toast.error("Email not available for resend");
                return;
            }

            const response = await dispatch(changeEmail({ new_email: targetEmail })).unwrap();
            const message = response?.message || response?.detail || "Verification code sent!";
            toast.success(message);
            setTimer(RESEND_DURATION);
            setOtp(["", "", "", "", "", ""]);
            inputRefs.current[0]?.focus();
        } catch (error) {
            const errorMessage =
                (Array.isArray(error?.new_email) && error.new_email[0]) ||
                error?.new_email ||
                error?.detail ||
                error?.error ||
                error?.message ||
                "Failed to resend code";
            toast.error(errorMessage);
        } finally {
            setResending(false);
        }
    };

    // Handle input change
    const handleChange = (e, idx) => {
        const value = e.target.value.replace(/[^0-9]/g, "");
        if (!value) return;

        let newOtp = [...otp];

        // If user pastes all at once
        if (value.length === 6) {
            newOtp = value.split("").slice(0, 6);
            setOtp(newOtp);
            inputRefs.current[5]?.focus();
            return;
        }

        newOtp[idx] = value[0];
        setOtp(newOtp);

        // Move focus to next input
        if (idx < 5 && value) {
            inputRefs.current[idx + 1]?.focus();
        }
    };

    // Handle backspace and arrow navigation
    const handleKeyDown = (e, idx) => {
        if (e.key === "Backspace") {
            if (otp[idx]) {
                const newOtp = [...otp];
                newOtp[idx] = "";
                setOtp(newOtp);
            } else if (idx > 0) {
                inputRefs.current[idx - 1]?.focus();
            }
        } else if (e.key === "ArrowLeft" && idx > 0) {
            inputRefs.current[idx - 1]?.focus();
        } else if (e.key === "ArrowRight" && idx < 5) {
            inputRefs.current[idx + 1]?.focus();
        }
    };

    // Handle paste
    const handlePaste = (e) => {
        const paste = e.clipboardData.getData("text").replace(/[^0-9]/g, "");
        if (paste.length === 6) {
            setOtp(paste.split("").slice(0, 6));
            inputRefs.current[5]?.focus();
            e.preventDefault();
        }
    };

    // Handle verify
    const handleVerify = async () => {
        const otpCode = otp.join("");

        if (otpCode.length !== 6) {
            toast.error("Please enter complete 6-digit code");
            return;
        }

        if (!targetEmail) {
            toast.error("Email not available for verification");
            return;
        }

        setLoading(true);
        try {
            const response = await dispatch(verifyEmailOtp({ new_email: targetEmail, otp: otpCode })).unwrap();
            const message = response?.message || response?.detail || "Email verified successfully!";
            toast.success(message);

            // Refresh user profile so UI reflects the new email
            try {
                await dispatch(AccountProfile());
            } catch (profileError) {
                // Non-blocking: still navigate even if profile refresh fails
                console.error("Failed to refresh profile after email verify", profileError);
            }
            setTimeout(() => {
                navigate("/settings/account/details");
            }, 1000);
        } catch (error) {
            const errorMessage =
                (Array.isArray(error?.otp) && error.otp[0]) ||
                error?.otp ||
                error?.detail ||
                error?.error ||
                error?.message ||
                "Verification failed. Please try again.";
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const isOtpComplete = otp.every(digit => digit !== "");

    return (
        <div className="bg-white dark:bg-[#1E1D21] min-h-screen">
            {/* Header */}
            <div className="sticky top-0 z-50">
                <div className="relative flex items-center justify-center h-12 bg-[#BDE6FF] rounded-b-xl px-2">
                    <IoMdArrowBack
                        onClick={() => navigate("/settings/account/details")}
                        className="absolute left-2 top-1/2 transition-transform -translate-y-1/2 text-[24px] text-black cursor-pointer"
                    />
                    <p className="text-center font-bold text-[16px] text-black flex-grow">
                        Verify Your Email
                    </p>
                </div>
            </div>

            {/* Content */}
            <div className="flex flex-col items-center px-4 pt-10">
                <p className="text-[14px] text-center text-black dark:text-white mb-2">
                    We've sent a verification code to your email
                </p>

                <p className="text-[16px] font-semibold text-black dark:text-white mb-8">
                    {newEmail || User_profile?.email}
                </p>

                {/* OTP Input Section */}
                <div className="w-full mb-4">
                    <p className="text-[14px] text-center text-black dark:text-white font-semibold mb-4">
                        Enter 6-Digit Code
                    </p>

                    <div className="flex justify-center gap-2 px-2 mb-4">
                        {otp.map((digit, idx) => (
                            <input
                                key={idx}
                                type="text"
                                maxLength={1}
                                value={digit}
                                inputMode="numeric"
                                onPaste={handlePaste}
                                autoFocus={idx === 0}
                                onChange={e => handleChange(e, idx)}
                                onKeyDown={e => handleKeyDown(e, idx)}
                                ref={el => inputRefs.current[idx] = el}
                                className="w-10 h-10 text-center text-[20px] font-bold bg-white dark:bg-[#333333] text-black dark:text-white border-2 border-[#BDE6FF] dark:border-gray-800/50 rounded-lg focus:border-[#4cb6f9] focus:outline-none transition-colors"
                            />
                        ))}
                    </div>
                </div>

                {/* Timer / Resend Section */}
                <div className="flex flex-col items-center gap-2 mb-8">
                    {timer > 0 ? (
                        <p className="text-[14px] text-gray-600 dark:text-gray-400">
                            Code expires in: <span className="text-[#4cb6f9] font-semibold">{formatTime(timer)}</span>
                        </p>
                    ) : (
                        <p className="text-[14px] text-gray-600 dark:text-gray-400">
                            Code expired
                        </p>
                    )}

                    <div className="text-[14px] text-gray-600 dark:text-gray-400">
                        Didn't receive the code?{" "}
                        <button
                            onClick={handleResendOtp}
                            disabled={resending || timer > 0}
                            className="text-[#4cb6f9] font-semibold hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {resending ? "Sending..." : "Resend Code"}
                        </button>
                    </div>
                </div>

                {/* Verify Button */}
                <button
                    onClick={handleVerify}
                    disabled={!isOtpComplete || loading}
                    className={`w-full max-w-md py-3 rounded-lg text-[15px] font-bold transition-all ${isOtpComplete && !loading
                            ? "bg-[#BDE6FF] text-black cursor-pointer"
                            : "bg-gray-300 dark:bg-[#333333] text-gray-500 dark:text-gray-400 cursor-not-allowed"
                        }`}
                >
                    {loading ? (
                        <BeatLoader size={10} color={"#000"} loading={loading} />
                    ) : (
                        "Verify Email Address"
                    )}
                </button>

                {/* Change Email Link */}
                <button
                    onClick={() => navigate("/settings/account/email")}
                    className="text-[#4cb6f9] font-semibold text-[14px] mt-6 hover:underline"
                >
                    Change Email Address
                </button>
            </div>
        </div>
    );
};

export default VerifyEmail;
